"use client";

import { createContext, useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type Ref, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Filter, X } from "lucide-react";
import { Button } from "~/components/ui/button";

export const HeaderFilterPortal = createContext<RefObject<HTMLDivElement> | null>(null);

export function HeaderFilter({ label, heading, compact = false, summary, description = summary, onClear, triggerRef, preferredWidth = 280, children }: { label: string; heading: ReactNode; compact?: boolean; summary?: string; description?: string; onClear: () => void; triggerRef?: Ref<HTMLButtonElement>; preferredWidth?: 280 | 350; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const summaryId = useId();
  const descriptionId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const repositionPanel = useRef<(() => void) | null>(null);
  const [position, setPosition] = useState({ left: 0, top: 0, maxHeight: 0 });
  useLayoutEffect(() => {
    if (!open || !trigger.current) return;
    const reposition = (event?: Event) => {
      const bounds = trigger.current!.getBoundingClientRect();
      if (event) {
        let left = Math.max(0, bounds.left);
        let right = Math.min(window.innerWidth, bounds.right);
        let top = Math.max(0, bounds.top);
        let bottom = Math.min(window.innerHeight, bounds.bottom);
        // Root/body overflow is propagated to the viewport, already intersected above.
        // A nested selector's body scroll lock must not clip against a zero-height body.
        for (let ancestor = trigger.current!.parentElement; ancestor && ancestor !== document.body && ancestor !== document.documentElement; ancestor = ancestor.parentElement) {
          const style = getComputedStyle(ancestor);
          const clip = ancestor.getBoundingClientRect();
          if (/auto|scroll|hidden|clip/.test(style.overflowX)) {
            left = Math.max(left, clip.left + ancestor.clientLeft);
            right = Math.min(right, clip.left + ancestor.clientLeft + ancestor.clientWidth);
          }
          if (/auto|scroll|hidden|clip/.test(style.overflowY)) {
            top = Math.max(top, clip.top + ancestor.clientTop);
            bottom = Math.min(bottom, clip.top + ancestor.clientTop + ancestor.clientHeight);
          }
        }
        if (left >= right || top >= bottom) { setOpen(false); return; }
      }
      const below = Math.max(0, window.innerHeight - bounds.bottom - 16);
      const above = Math.max(0, bounds.top - 16);
      const height = panel.current?.scrollHeight ?? 0;
      const flip = height > below && above > below;
      const maxHeight = flip ? above : below;
      const width = Math.min(preferredWidth, window.innerWidth - 16);
      const next = { left: Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8)),
        top: flip ? Math.max(8, bounds.top - 8 - Math.min(height, maxHeight)) : bounds.bottom + 8, maxHeight };
      setPosition((current) => current.left === next.left && current.top === next.top && current.maxHeight === next.maxHeight ? current : next);
    };
    repositionPanel.current = reposition;
    reposition();
    window.addEventListener("resize", reposition);
    document.addEventListener("scroll", reposition, true);
    const sizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(() => reposition());
    if (panel.current) sizeObserver?.observe(panel.current);
    return () => {
      repositionPanel.current = null;
      window.removeEventListener("resize", reposition);
      document.removeEventListener("scroll", reposition, true);
      sizeObserver?.disconnect();
    };
  }, [open, preferredWidth]);
  // Header content can move an anchor without resizing the panel itself.
  useLayoutEffect(() => { if (open) repositionPanel.current?.(); });
  useImperativeHandle(triggerRef, () => trigger.current!);
  useEffect(() => {
    if (open) panel.current?.querySelector<HTMLElement>("input:not([type=hidden]):not([aria-hidden=true]), [role=combobox], button[role=checkbox]")?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    let listening = true;
    const dismissOutside = (event: Event) => {
      if (event.target instanceof Node && !panel.current?.contains(event.target) && !trigger.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const dismissOnBlur = (event: FocusEvent) => {
      if (event.relatedTarget instanceof Node && (panel.current?.contains(event.relatedTarget) || trigger.current?.contains(event.relatedTarget))) return;
      const nestedChoice = event.target instanceof Element ? event.target.closest('[role="listbox"]') : null;
      const ownedNestedChoice = nestedChoice && panel.current?.contains(nestedChoice) ? nestedChoice : null;
      queueMicrotask(() => {
        // An owned selector removes its focused option before restoring its trigger.
        // Actual focus entering the page is still handled by dismissOutside.
        if (event.relatedTarget === null && ownedNestedChoice && !ownedNestedChoice.isConnected) return;
        if (listening && !panel.current?.contains(document.activeElement) && !trigger.current?.contains(document.activeElement)) {
          setOpen(false);
        }
      });
    };
    document.addEventListener("pointerdown", dismissOutside, true);
    document.addEventListener("focusin", dismissOutside);
    document.addEventListener("focusout", dismissOnBlur);
    return () => {
      listening = false;
      document.removeEventListener("pointerdown", dismissOutside, true);
      document.removeEventListener("focusin", dismissOutside);
      document.removeEventListener("focusout", dismissOnBlur);
    };
  }, [open]);
  const closeAndFocusTrigger = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  const onEscape = (event: KeyboardEvent<HTMLElement>) => {
    if (open && event.key === "Escape" && !event.defaultPrevented) {
      event.preventDefault();
      event.stopPropagation();
      closeAndFocusTrigger();
    }
    if (!open || event.key !== "Tab" || event.defaultPrevented) return;
    const selector = "button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]";
    const usable = (element: HTMLElement) => element.tabIndex >= 0 && !element.closest("[hidden], [inert]");
    const fields = Array.from(panel.current?.querySelectorAll<HTMLElement>(selector) ?? []).filter(usable);
    if (event.shiftKey && document.activeElement === fields[0]) {
      event.preventDefault();
      trigger.current?.focus();
    } else if (!event.shiftKey && document.activeElement === trigger.current) {
      event.preventDefault();
      fields[0]?.focus();
    } else if (!event.shiftKey && document.activeElement === fields[fields.length - 1]) {
      const pageControls = Array.from(document.querySelectorAll<HTMLElement>(selector))
        .filter((element) => usable(element) && !panel.current?.contains(element));
      const next = pageControls[pageControls.indexOf(trigger.current!) + 1];
      if (next) { event.preventDefault(); next.focus(); }
    }
  };
  return <div className="min-w-0 space-y-1">
    <div className={`flex min-h-10 min-w-0 items-center ${compact ? "gap-0.5" : "gap-1"}`}>
      <div className="flex min-w-0 items-center">{heading}</div>
      <Button ref={trigger} type="button" variant="ghost" size="icon" aria-label={`Фильтр: ${label}`}
        aria-expanded={open} aria-controls={panelId} aria-haspopup="dialog" aria-describedby={summary ? (description !== summary ? descriptionId : summaryId) : undefined}
        className={`shrink-0${compact ? " w-6" : ""}${summary ? " text-primary" : ""}`} onClick={() => setOpen(!open)} onKeyDown={onEscape}>
        <Filter className="size-4" aria-hidden="true" />
      </Button>
    </div>
    {summary ? <p id={summaryId} className="h-4 truncate text-xs font-normal text-primary" title={description}>{summary}</p> : <span aria-hidden="true" className="block h-4" />}
    {summary && description !== summary && <span id={descriptionId} className="sr-only">{description}</span>}
    {open && createPortal(<div ref={panel} id={panelId} role="dialog" aria-label={`Фильтр: ${label}`} onKeyDown={onEscape}
      className="fixed z-50 flex max-w-[calc(100vw-16px)] flex-col gap-3 rounded-md border bg-popover p-3 text-popover-foreground shadow-md"
      style={{ ...position, width: preferredWidth }}>
      <div className="flex shrink-0 items-center justify-between gap-3">
        <span className="text-sm font-medium leading-5">{label}</span>
        <Button className="shrink-0" type="button" variant="ghost" size="icon" aria-label={`Закрыть фильтр: ${label}`} onClick={closeAndFocusTrigger}><X aria-hidden="true" /></Button>
      </div>
      <HeaderFilterPortal.Provider value={panel}><div className="-m-1 min-h-0 overflow-auto p-1">{children}</div></HeaderFilterPortal.Provider>
      <Button className="shrink-0" type="button" variant="outline" onClick={onClear}>Очистить фильтр</Button>
    </div>, document.body)}
  </div>;
}
